import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext/useCatalogMetricsContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { getProspectiveBudget } from 'src/modals/SponsoredListings/sponsoredListings.util';
import {
  getEventPerformerVenue,
  getFormattedEventName,
} from 'src/utils/eventWithDataUtils';
import {
  posChangedField,
  posField,
  posFieldFromValues,
  resetPosFields,
} from 'src/utils/posFieldUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BiddingMode,
  CampaignManagerClient,
  FlightDuration,
  ListingMetrics,
  SponsoredListingsCampaign,
  SponsoredListingsCampaignInput,
} from 'src/WebApiController';

export const useSponsoredListingCampaignForm = ({
  eventId,
  campaign,
  onBeforeSubmit,
  onAfterSubmit,
  onError,
  resetFormAfterSubmitSuccess,
}: {
  eventId?: number;
  campaign?: SponsoredListingsCampaign | null;
  onBeforeSubmit?: () => void;
  onAfterSubmit?: (success: boolean) => void;
  onError?: (error: unknown) => void;
  resetFormAfterSubmitSuccess?: boolean;
}) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const { data } = useCatalogDataContext();
  const { event, performer, venue } = useMemo(
    () => getEventPerformerVenue(eventId?.toString() ?? '', data),
    [data, eventId]
  );
  const { eventMetrics } = useCatalogMetricsContext<ListingMetrics>();
  const metrics = useMemo(
    () =>
      event?.event?.virtId ? eventMetrics?.[event.event.virtId] : undefined,
    [eventMetrics, event?.event?.virtId]
  );

  const [mainText, connectText, subText] = getFormattedEventName({
    event: event?.event,
    performer,
    venue,
  });

  const eventNameDisplay =
    `${mainText} ${connectText?.trim()} ${subText}`.trim();

  const getDefaultValue = useCallback(
    (campaign?: SponsoredListingsCampaign | null) => {
      const eventEndDate =
        event?.event?.datesUtc?.end ?? event?.event?.dates?.end;
      const eventStartDate =
        event?.event?.datesUtc?.start ?? event?.event?.dates?.start;

      return {
        eventId,
        campaignId: campaign?.campaignId ?? -1, // negative value indicates new campaign
        biddingMode: posFieldFromValues(
          campaign?.biddingMode ?? BiddingMode.Automatic,
          campaign?.biddingMode
        ),
        baseBid: posFieldFromValues(campaign?.baseBid, campaign?.baseBid),
        targetTicketClassIds: posFieldFromValues(
          campaign?.targetTicketClassIds,
          campaign?.targetTicketClassIds
        ),
        firstThreeRowsOnly: posFieldFromValues(
          campaign?.firstThreeRowsOnly ?? false,
          campaign?.firstThreeRowsOnly
        ),
        campaignName: posFieldFromValues(
          campaign?.campaignName ||
            `${eventNameDisplay} - ${FlightDuration.Daily}`,
          campaign?.campaignName
        ),
        flightDuration: posFieldFromValues(
          campaign?.flightDuration || FlightDuration.Daily,
          campaign?.flightDuration
        ),
        recurringFlightBudget: posFieldFromValues(
          campaign?.recurringFlightBudget ||
            getProspectiveBudget(
              metrics?.unsoldCst,
              FlightDuration.Daily,
              eventStartDate
            ),
          campaign?.recurringFlightBudget
        ),
        currentFlightBudget: posField(campaign?.activeFlight?.flightBudget),
        dateRange: campaign?.dateRange || {
          start: new Date().toISOString(),
          end: eventEndDate,
        },
        editBudgetForActiveFlightOnly: campaign?.activeFlight
          ? posChangedField(true)
          : posField(false),
      };
    },
    [
      event?.event?.datesUtc?.start,
      event?.event?.datesUtc?.end,
      event?.event?.dates?.start,
      event?.event?.dates?.end,
      eventNameDisplay,
      eventId,
      metrics?.unsoldCst,
    ]
  );

  const methods = useForm<SponsoredListingsCampaignInput>({
    defaultValues: getDefaultValue(campaign),
  });

  const onSubmit = useCallback(
    async (formValue: SponsoredListingsCampaignInput) => {
      onBeforeSubmit?.();
      tryInvokeApi(
        async () => {
          const client = new CampaignManagerClient(
            activeAccountWebClientConfig
          );
          const result = await client.mergeCampaign(formValue);
          if (!result.success) {
            showErrorDialog('Sponsored Listing', {
              message: result.errorMessage,
            });
            return;
          }

          onAfterSubmit?.(true);
          if (resetFormAfterSubmitSuccess) {
            methods.reset(resetPosFields(formValue));
          }
        },
        (error) => {
          showErrorDialog('Sponsored Listing', error, {
            trackErrorData: formValue,
          });
          onError?.(error);
        },
        () => {
          onAfterSubmit?.(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      methods,
      onAfterSubmit,
      onBeforeSubmit,
      onError,
      resetFormAfterSubmitSuccess,
      showErrorDialog,
    ]
  );

  return {
    methods,
    onSubmit,
    event,
    eventNameDisplay,
    metrics,
    getDefaultValue,
  };
};
